import { useState, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
// hooks
import { useAuth } from 'src/hooks/auth/useAuth';
import { useProfile } from 'src/hooks/profile/useProfile';
// components
import LoadingBar from '../components/Reusable/LoadingBar';
import { PATH_AUTH } from 'src/routes/paths';

// ----------------------------------------------------------------------

type AuthGuardProps = {
    children: ReactNode;
};

export default function AuthGuard({ children }: AuthGuardProps) {
    const { isAuthenticated, isInitialized } = useAuth();
    const { loading: profileLoading } = useProfile();

    const { pathname } = useLocation();
    const [requestedLocation, setRequestedLocation] = useState<string | null>(null);

    // Show loading bar while auth is initializing or profile has no usable data yet
    if (!isInitialized || (isAuthenticated && profileLoading)) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    bgcolor: 'background.default'
                }}
            >
                <LoadingBar />
            </Box>
        );
    }

    // Handle not authenticated case - redirect to login route
    if (!isAuthenticated) {
        if (pathname !== requestedLocation) {
            setRequestedLocation(pathname);
        }
        return <Navigate to={PATH_AUTH.login} replace />;
    }

    // Handle redirect after authentication
    if (requestedLocation && pathname !== requestedLocation) {
        setRequestedLocation(null);
        return <Navigate to={requestedLocation} />;
    }

    return <>{children}</>;
}
